import type { List, Record } from '@gonfalon/immutable-shim/immutableShim';
import type { ImmutableMap } from '@gonfalon/legacy-immutable/immutableUtils';
import type { ResourceSpecifier } from '@gonfalon/resource-specifiers/ast';

import { allowDecision } from '#utils/access/allowDecision';
import { createAccessDecision } from '#utils/access/records/AccessDecision';
import type { AccessChecks, CheckAccessFunction } from '#utils/access/types';
import type { ApprovalSettings } from '#utils/environments/records/ApprovalSettings';
import type { Environment } from '#utils/environments/records/Environment';
import type { ResourceApprovalSettings } from '#utils/environments/records/ResourceApprovalSettings';
import type { FlagCopyResourceType } from '#utils/flagComparisonUtils';
import type { Flag } from '#utils/flags/records/Flag';
import type { Member } from '#utils/members/records/Member';
import type { WorkflowNotificationKind } from '#utils/pendingChanges/constants';
import type { Project } from '#utils/projects/records/Project';
import type { Segment } from '#utils/segment/records/Segment';

import type { AiConfig } from '../aiConfigUtils';
import type { SemanticInstruction } from '../instructions/shared/types';
import type { WorkflowSemanticInstruction } from '../workflowUtils';

import {
  type ApprovalInstructionKindForMultipleValueResources,
  type ApprovalsReviewKind,
  type ApprovalsReviewStatus,
} from './constants';
import { type ApprovalCustomWorkflowMetadata } from './records/ApprovalCustomWorkflowMetadata';
import { type ApprovalRequest } from './records/ApprovalRequest';
import { type ApprovalRequestReview } from './records/ApprovalRequestReview';

export type ApprovalProgressStatusKey = 'PENDING' | 'APPROVED' | 'COMPLETED' | 'DECLINED' | 'FAILED' | '';

export type ApprovalRequestConflict = ImmutableMap<{
  instruction: SemanticInstruction;
  reason: string;
}>;

export type IntegrationMetadata = ImmutableMap<{
  externalId: string;
  externalStatus: ImmutableMap<{
    display: string;
    value: string;
  }>;
  externalUrl: string;
  lastChecked: number;
}>;

export type ApprovalRequestResourceKind = 'Flag' | 'FlagConfiguration' | 'Segment' | 'AiConfig' | 'Experiment';
export type ResourceTypeName = ResourceSpecifier['type'] | 'resource';

export class ExperimentApprovalResource {
  key: string;
  name: string;
  _access?: AccessChecks;

  constructor({ key, name, _access }: { key: string; name: string; _access?: AccessChecks }) {
    this.key = key;
    this.name = name;
    this._access = _access;
  }

  checkAccess(profile: Member): CheckAccessFunction {
    const access = this._access;

    if (profile.isReader()) {
      return () =>
        createAccessDecision({
          isAllowed: false,
          appliedRoleName: 'Reader',
        });
    }

    if (profile.isAdmin() || profile.isWriter() || profile.isOwner() || !access || !access.get('denied')) {
      return allowDecision;
    }

    return (action) => {
      const deniedAction = access.get('denied').find((v) => v.get('action') === action);
      if (deniedAction) {
        const reason = deniedAction.get('reason');
        const roleName = reason && reason.get('role_name');
        return createAccessDecision({
          isAllowed: false,
          appliedRoleName: roleName,
        });
      }
      return createAccessDecision({ isAllowed: true });
    };
  }
}

export type ApprovalRequestProps = {
  _id: string;
  _links: ImmutableMap<{
    self: string;
    review: string;
  }>;
  reviewStatus: ApprovalsReviewStatus;
  creationDate: number;
  requestorId: string;
  description: string;
  allReviews: List<ApprovalRequestReview>;
  notifyMemberIds: List<string>;
  executionDate: number;
  appliedDate: number;
  instructions: List<WorkflowSemanticInstruction>;
  conflicts: List<ApprovalRequestConflict>;
  status: string;
  appliedByMemberId?: string;
  appliedByServiceTokenId?: string;
  serviceKind: string;
  integrationMetadata: IntegrationMetadata;
  operatingOnId?: string;
  source?: ImmutableMap<FlagCopyResourceType>;
  customWorkflowMetadata?: ApprovalCustomWorkflowMetadata;
  project?: Project;
  environment?: Environment;
  environments?: List<Environment>;
  flag?: Flag;
  approvalSettings?: ApprovalSettings;
  resourceApprovalSettings?: ResourceApprovalSettings;
  resourceId?: string;
  resource?: ImmutableMap<{
    kind: string;
    flag?: Flag | null;
    segment?: Segment | null;
    aiConfig?: AiConfig | null;
    experiment?: ExperimentApprovalResource | null;
  }>;
};

export type ReviewerType = 'member' | 'serviceToken';

export type ApprovalRequestReviewProps = {
  _id: string;
  creationDate: number;
  kind: ApprovalsReviewKind;
  memberId?: string;
  serviceTokenId?: string;
  comment: string;
  workflowVersion: number;
};

export type ApprovalRequestOptions = { shouldNotify?: boolean; notificationKind?: WorkflowNotificationKind };

export type ReviewersCount = {
  numberOfTeams: number;
  numberOfMembers: number;
  totalMembersCount: number;
};

export type ApprovalsSemanticPatchInstructions = { kind: ApprovalInstructionKindForMultipleValueResources } & {
  notifyMemberIds?: string[];
  notifyTeamKeys?: string[];
};

export type ApprovalRequestSummaryProps = ApprovalRequestProps &
  (
    | {
        project: Project;
        environment: Environment;
        flag: Flag;
      }
    | { project: Project; environments: List<Environment>; flag: Flag }
  );

export type ApprovalRequestSummary = ReturnType<Record.Factory<ApprovalRequestSummaryProps>> &
  InstanceType<typeof ApprovalRequest>;

export type ApprovalRequestsResponse<T extends ApprovalRequest = ApprovalRequest> = Readonly<{
  entities: Readonly<{ approvalRequests?: Readonly<{ [_id: string]: T }> }>;
  result: Readonly<{ items?: readonly string[]; totalCount: number; links: { self: { href: string; type: string } } }>;
}>;

export type ApprovalRequestSummariesResponse = ApprovalRequestsResponse<ApprovalRequestSummary>;
export type ApprovalRequestSummariesListResponse = Readonly<
  Omit<ApprovalRequestSummariesResponse['result'], 'items'> & {
    items: readonly ApprovalRequestSummary[];
  }
>;
